import { useToast } from '@orbind/ui'
import { useMutation, useQuery } from '@tanstack/react-query'
import { desktopApps } from '../app/desktop.ts'
import { api, webTokenStorage } from '../../lib/api.ts'

/**
 * Workspaces do usuario: o ativo, a lista para alternar e o atalho de criar.
 *
 * A WorkspaceSection aparece em DOIS lugares (decisao de 2026-08-15, pedido de
 * tornar a troca acessivel): no menu do nome do workspace (topo do explorer,
 * padrao Slack) e no popover de perfil. Criar workspace e a tela FormScreen
 * /workspace/new — o modal antigo foi aposentado.
 *
 * Trocar ou criar reemite o token no servidor; aqui so gravamos o token novo e
 * recarregamos a pagina. O reload e deliberado: TODO dado em cache (React
 * Query, estado de tela, aplicacoes abertas) pertence ao workspace anterior, e
 * derrubar tudo de uma vez e mais honesto do que cacar cada cache um a um.
 */

export type Workspace = {
  id: string
  name: string
  slug: string
  role: string
  isCurrent: boolean
  /** O workspace tem logo proprio (a imagem em si sai por /public/workspace-logo). */
  hasLogo: boolean
  /** Cache-buster do logo: muda a cada alteracao do workspace. */
  logoVersion: string
}

export function useWorkspaces() {
  return useQuery({
    queryKey: ['workspaces'],
    queryFn: async () => {
      const resposta = await api.workspaces.$get()
      const dados = (await resposta.json()) as { workspaces: Workspace[] }
      return dados.workspaces
    },
    staleTime: 5 * 60 * 1000,
  })
}

/** Aplica a sessao nova e recomeça do zero no workspace escolhido. */
export async function adotarSessao(token: string) {
  // As webviews NATIVAS do desktop sobrevivem ao reload da pagina — sao janelas
  // filhas, nao DOM. As aplicacoes abertas pertencem ao workspace anterior,
  // entao fecham aqui, antes do recomeco (SPEC-20260813-2159).
  try {
    const abertas = await desktopApps.abertas()
    await Promise.allSettled(abertas.map((id) => desktopApps.fechar(id)))
  } catch {
    // sem ponte (web) ou falha ao fechar: nada disso pode impedir a troca
  }
  webTokenStorage.set(token)
  window.location.assign('/home')
}

/** Lista dos workspaces com troca de sessao + atalho para criar um novo. */
export function WorkspaceSection({ onCriar }: { onCriar: () => void }) {
  const toast = useToast()
  const { data: workspaces } = useWorkspaces()

  const trocar = useMutation({
    mutationFn: async (id: string) => {
      const resposta = await api.workspaces[':id'].switch.$post({ param: { id } })
      return (await resposta.json()) as { token: string; workspace: { name: string } }
    },
    onSuccess: (dados) => void adotarSessao(dados.token),
    onError: () => toast.push({ tone: 'danger', message: 'Não foi possível trocar de workspace.' }),
  })

  return (
      <div className="wsmenu">
        <span className="wsmenu__rotulo">Workspace</span>
        {(workspaces ?? []).map((w) => (
          <button
            key={w.id}
            type="button"
            role="menuitem"
            className={`wsmenu__item${w.isCurrent ? ' wsmenu__item--atual' : ''}`}
            disabled={w.isCurrent || trocar.isPending}
            onClick={() => trocar.mutate(w.id)}
            title={w.isCurrent ? 'Workspace atual' : `Trabalhar em ${w.name}`}
          >
            <i className={w.isCurrent ? 'fas fa-check-circle' : 'far fa-circle'} aria-hidden />
            <span className="wsmenu__nome">{w.name}</span>
            {w.role === 'owner' ? <span className="wsmenu__papel">dono</span> : null}
          </button>
        ))}
        <button
          type="button"
          role="menuitem"
          className="wsmenu__item wsmenu__item--novo"
          onClick={onCriar}
        >
          <i className="fas fa-plus" aria-hidden />
          Criar workspace
        </button>
      </div>
  )
}
